import { type ChangeEvent, useMemo } from 'react';

import { Box, IconButton, Input, Text } from '@zillow/constellation-10';
import { IconMinusFilled, IconPlusFilled } from '@zillow/constellation-icons';

import { RentalManagerListingCriteriaType } from '@zg-rentals/ts-rental-manager-api-beta-client';

import { EVENT_IDS } from '../../../analytics/events/screeningCriteria';

type MockEventProps = {
  target: {
    name: string;
    value: string | boolean;
    checked?: string | boolean;
  };
  persist?: () => void;
};

type SaveEvent = ChangeEvent<HTMLInputElement> | MockEventProps;

type Props = {
  label: string;
  name: string;
  value: string;
  onChange?: (event: SaveEvent) => void;
  onMinusClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onPlusClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const PetRow = ({ label, name, value, onChange, onMinusClick, onPlusClick }: Props) => {
  const dataEventId = useMemo(() => {
    if (name === RentalManagerListingCriteriaType.PET_POLICY_CAT_COUNT) {
      return EVENT_IDS.NUMBER_OF_CATS_ALLOWED;
    }
    if (name === RentalManagerListingCriteriaType.PET_POLICY_SMALL_DOG_COUNT) {
      return EVENT_IDS.NUMBER_OF_SMALL_DOGS_ALLOWED;
    }
    if (name === RentalManagerListingCriteriaType.PET_POLICY_LARGE_DOG_COUNT) {
      return EVENT_IDS.NUMBER_OF_LARGE_DOGS_ALLOWED;
    }
    return undefined;
  }, [name]);

  const decreaseLabel = `Decrease number of ${label.toLowerCase()}`;
  const increaseLabel = `Increase number of ${label.toLowerCase()}`;
  const inputLabel = `Number of ${label.toLowerCase()}`;

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
      data-testid="PetsAllowedRow"
    >
      <Text asChild>
        <p>{label}</p>
      </Text>
      <Box
        data-testid="PetCountInputContainer"
        css={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <IconButton
          shape="circle"
          tone="neutral"
          emphasis="bare"
          icon={<IconMinusFilled />}
          name={name}
          size="sm"
          title="Minus"
          aria-label={decreaseLabel}
          onClick={onMinusClick}
          data-eventid={dataEventId}
        />
        <Input
          readOnly
          size="sm"
          placeholder="0"
          name={name}
          value={value}
          onChange={onChange}
          aria-label={inputLabel}
          css={{
            width: '48px',
            textAlign: 'center',
          }}
        />
        <IconButton
          shape="circle"
          tone="neutral"
          emphasis="bare"
          icon={<IconPlusFilled />}
          name={name}
          size="sm"
          title="Plus"
          aria-label={increaseLabel}
          onClick={onPlusClick}
          data-eventid={dataEventId}
        />
      </Box>
    </Box>
  );
};

export default PetRow;
